import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { keyBy } from 'lodash';
import { ExternalLink } from 'lucide-react';

import { Tooltip } from '@/components/ui/tooltip';
import type { Task } from '@/project-management/entities/task';
import { TaskPriority } from '@/project-management/entities/task-priority';
import { getTaskLink } from '@/project-management/utils/get-task-link';
import { TaskPriorityBadge } from '@/task-manager/components/task-priority-badge';
import { cls } from '@/utils/cls';

import { TaskBadge } from './task-badge';

export type TimelineTaskProps = {
  date: Date;
  tasks: Pick<
    Task,
    'id' | 'title' | 'dueDate' | 'status' | 'priority' | 'assignee'
  >[];
  tooltipPosition?: 'top' | 'left' | 'right' | 'bottom';
  className?: string;
  style?: CSSProperties;
};

export function TimelineTask({
  tasks,
  tooltipPosition,
  className,
  style,
}: TimelineTaskProps) {
  const taskPerPriority = keyBy(tasks, 'priority');

  const badgeTasks = [
    taskPerPriority[TaskPriority.HIGH],
    taskPerPriority[TaskPriority.MEDIUM],
    taskPerPriority[TaskPriority.LOW],
  ].filter(Boolean);

  const [firstBadgeTask, secondBadgeTask, thirdBadgeTask] = badgeTasks;

  const tooltip = tasks.map((task) => (
    <div key={task.id} className="space-y-2 px-3 py-2 text-xs">
      <div className="flex items-center justify-start gap-1">
        <ExternalLink className="size-3 shrink-0" />
        <Link
          to={getTaskLink({ taskId: task.id })}
          className="grow break-words font-semibold hover:underline"
        >
          {task.title}
        </Link>
      </div>
      <div className="grid grid-cols-[min-content_1fr_min-content] items-center gap-2">
        <TaskPriorityBadge priority={task.priority} className="shrink-0" />
        <div>Assignee: {task.assignee ?? '-'}</div>
        <div className="shrink-0 whitespace-nowrap text-nowrap">
          {task.dueDate ? <>{format(task.dueDate, 'MMM d')}</> : null}
        </div>
      </div>
    </div>
  ));

  return (
    <div className={cls('group relative', className)} style={style}>
      <Tooltip
        delay
        clickToKeepOpen
        position={tooltipPosition}
        className="max-h-80 max-w-60 divide-y divide-gray-100 overflow-y-auto p-0"
        content={tooltip}
      >
        {thirdBadgeTask ? (
          <TaskBadge
            task={thirdBadgeTask}
            className="absolute right-0 top-0 -z-20 translate-x-[-2px] cursor-pointer transition-transform group-hover:translate-x-[-4px]"
          />
        ) : null}
        {secondBadgeTask ? (
          <TaskBadge
            task={secondBadgeTask}
            className="absolute left-0 top-0 -z-10 translate-x-[2px] cursor-pointer transition-transform group-hover:translate-x-[4px]"
          />
        ) : null}
        <TaskBadge
          task={firstBadgeTask}
          className="cursor-pointer"
          clickable={!secondBadgeTask && !thirdBadgeTask}
        />
      </Tooltip>
    </div>
  );
}
